import React from 'react';
import { Typography, Toolbar, Divider, Box, Grid, Paper } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { DataGrid, GridToolbar, type GridColDef } from '@mui/x-data-grid';
import type { GridRenderCellParams } from '@mui/x-data-grid';
import type { UsageMetricData } from '@/types';

interface UsageMetricsDisplayProps {
  data: UsageMetricData | null;
  label: string;
  loading: boolean;
  error: string | null;
}

interface ChartDataItem {
  id: number;
  date: string;
  value: number;
  [key: string]: unknown;
}

function UsageMetricsDisplay({ data, label, loading, error }: UsageMetricsDisplayProps): React.JSX.Element {
  // Prepare data for chart and table
  const chartData: ChartDataItem[] =
    data?.data?.map((item, index) => {
      const chartItem: ChartDataItem = {
        id: index,
        date: item.date, // Keep original YYYY-MM-DD format
        value: typeof item.value === 'number' ? item.value : parseFloat(String(item.value)) || 0, // Convert string to number for chart
      };

      // Dynamically add all other properties from the item
      Object.keys(item).forEach((key) => {
        if (key !== 'date' && key !== 'value') {
          chartItem[key] = item[key];
        }
      });

      console.log('Created chartItem:', chartItem);
      return chartItem;
    }) || [];

  // Dynamically generate table columns based on the first data item
  const tableColumns: GridColDef<ChartDataItem>[] = React.useMemo(() => {
    if (!data?.data?.length) return [];

    const firstItem = data.data[0];
    const columns: GridColDef<ChartDataItem>[] = [
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
      },
    ];

    // Add columns for all properties except 'date' and 'value'
    Object.keys(firstItem).forEach((key) => {
      if (key !== 'date' && key !== 'value') {
        columns.push({
          field: key,
          headerName: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
          flex: 1,
          type: typeof firstItem[key] === 'number' ? 'number' : 'string',
          renderCell: (params: GridRenderCellParams<ChartDataItem>) => {
            console.log(
              'renderCell params:',
              params,
              'key:',
              key,
              'row:',
              params.row,
            );
            const value = params.row[key];
            console.log(
              'value for key',
              key,
              ':',
              value,
              'type:',
              typeof value,
            );

            // Handle null/undefined values
            if (value === null || value === undefined) {
              return 'not specified';
            }

            // Handle boolean values explicitly
            if (typeof value === 'boolean') {
              return value ? 'true' : 'false';
            }

            // Handle other values
            return String(value);
          },
        });
      }
    });

    // Add the value column at the end
    columns.push({
      field: 'value',
      headerName: 'Value',
      flex: 1,
      type: 'number',
    });

    return columns;
  }, [data?.data]);

  if (loading) {
    return (
      <>
        <Toolbar
          sx={{
            minHeight: '55px !important',
          }}
        >
          <Typography variant='h6' sx={{ mr: 4 }}>
            {label}
          </Typography>
        </Toolbar>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography>Loading...</Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Toolbar
          sx={{
            minHeight: '55px !important',
          }}
        >
          <Typography variant='h6' sx={{ mr: 4 }}>
            {label}
          </Typography>
        </Toolbar>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography color='error'>{error}</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <Typography variant='h6' sx={{ mr: 4 }}>
          {label}
        </Typography>
      </Toolbar>
      <Divider />

      <Box sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Bar Chart - Left Half */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                {label} by Date
              </Typography>
              {chartData.length > 0 ? (
                <BarChart
                  dataset={(() => {
                    // Find the categorical field (first field that's not date or value)
                    const categoricalField = Object.keys(
                      chartData[0] || {},
                    ).find(
                      (key) =>
                        key !== 'date' && key !== 'value' && key !== 'id',
                    );

                    if (!categoricalField) {
                      // Fallback to original data if no categorical field
                      return chartData as Array<Record<string, string | number>>;
                    }

                    // Get unique dates and categories
                    const uniqueDates = [
                      ...new Set(chartData.map((item) => item.date)),
                    ];
                    const uniqueCategories = [
                      ...new Set(
                        chartData.map((item) => item[categoricalField] as string),
                      ),
                    ];

                    // Create aggregated dataset
                    return uniqueDates.map((date) => {
                      const dateData: Record<string, string | number> = { date };

                      // For each category, sum up the values for this date
                      uniqueCategories.forEach((catValue) => {
                        const sum = chartData
                          .filter(
                            (item) =>
                              item.date === date &&
                              item[categoricalField] === catValue,
                          )
                          .reduce((total, item) => total + item.value, 0);
                        dateData[
                          `${categoricalField}_${catValue === null ? 'null' : catValue}`
                        ] = sum;
                      });

                      return dateData;
                    });
                  })()}
                  xAxis={[
                    {
                      dataKey: 'date',
                      scaleType: 'band',
                    },
                  ]}
                  series={(() => {
                    // Find the categorical field (first field that's not date or value)
                    const categoricalField = Object.keys(
                      chartData[0] || {},
                    ).find(
                      (key) =>
                        key !== 'date' && key !== 'value' && key !== 'id',
                    );

                    if (!categoricalField) {
                      // Fallback to single series if no categorical field
                      return [
                        {
                          dataKey: 'value',
                          label: label,
                        },
                      ];
                    }

                    // Get unique values for the categorical field
                    const uniqueValues = [
                      ...new Set(
                        chartData.map((item) => item[categoricalField] as string),
                      ),
                    ];

                    // Create series for each unique value
                    return uniqueValues.map((catValue) => ({
                      dataKey: `${categoricalField}_${catValue === null ? 'null' : catValue}`,
                      label: `${categoricalField}=${catValue === null ? 'null' : catValue}`,
                      stack: 'total',
                    }));
                  })()}
                  height={400}
                />
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  No data available for the selected time range.
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Table - Right Half */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Detailed Data
              </Typography>
              <Box sx={{ minHeight: '400px' }}>
                <DataGrid
                  rows={chartData}
                  columns={tableColumns}
                  getRowId={(row) => String(row.id || row.date)}
                  rowHeight={52}
                  showToolbar
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      sx: {
                        button: {
                          color: 'black',
                        },
                        svg: {
                          color: 'black',
                        },
                      },
                      showQuickFilter: true,
                    },
                  }}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10 },
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default UsageMetricsDisplay;
